import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ComponentDimensions } from '../../../../types/Layout';
import { colors } from '../../../../colors';
import { Subscription } from '../../../../types/subscription';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import CrosshairTooltip, {
  CROSSHAIR_TOOLTIP_WIDTH,
} from '../../../../components/Charts/CrosshairTooltip';
import useAnimatedChartData from '../../../../hooks/useAnimatedChartData';
import { OverviewChartTimeframe, OverviewChartType } from '../../types';
import { useSelector } from 'react-redux';
import { ChartType } from '../../../Detail/Charts/ProfitChart';
import LoadingIcons from 'react-loading-icons';
import {
  calculateLongestTickWidth,
  calculateYAxisWidth,
  calculateYTicks,
  stretchYValueOnSharedDomain,
} from '../../../../helpers/Chart';
import { subtractBaselineForwardFilled } from '../../../../helpers/Chart/subtractBaselineForwardFilled';
import {
  buildReferenceLineData,
  EXODUS_REFERENCE_LINE,
  MRC_REFERENCE_LINE,
  ReferenceLinePatterns,
  TPT_REFERENCE_LINE,
} from '../../../../helpers/Chart/referenceLines';
import moment from 'moment';
import {
  createXTickValues,
  getFormatUnit,
} from '../../../../helpers/bundleTransactionsToCompactForm';
import { formatPrice } from '../../../../helpers/NumberFormatter';
import {
  selectOverviewChartScale,
  selectOverviewChartTimeframe,
  selectOverviewChartType,
  selectOverviewValuesHidden,
} from '../../../../store/slices/ui';
import { selectUserId } from '../../../../store/slices/user';
import { selectIsDemo } from '../../../../store/slices/demo';
import { selectActiveCluster } from '../../../../store/slices/subscription';
import {
  useGetPortfolioChartFastQuery,
  useGetPortfolioChartQuery,
} from '../../../../store/services/api';
import { SummaryInformation as SummaryInformationType } from '../../types';
import useDemoChartPortfolio from '../../../../hooks/useDemoChartPortfolio';

/** Masks the digits of a formatted money string for the hidden-value toggle,
 *  keeping the $ sign and separators so the y-axis layout stays recognisable. */
const maskMonetaryValue = (value: string): string => value.replace(/\d/g, '•');

/** How long to wait for the (accurate) candle series before falling back to the
 *  fast transaction-point series. A warm candle request returns well under this. */
const FAST_FALLBACK_MS = 8000;

const SummaryChart = ({
  subscriptions,
  setSummaryInformation,
  totalValue,
}: {
  subscriptions: Subscription[];
  setSummaryInformation: React.Dispatch<
    React.SetStateAction<SummaryInformationType | undefined>
  >;
  totalValue: number;
}) => {
  const overviewChartTimeframe = useSelector(selectOverviewChartTimeframe);
  const overviewChartType = useSelector(selectOverviewChartType);
  const overviewChartScale = useSelector(selectOverviewChartScale);
  const valuesHidden = useSelector(selectOverviewValuesHidden);

  // Shifted-log y-transform. log10(LOG_PIVOT + y) compresses values near zero
  // and below — so the bottom decades occupy little space — and expands the
  // high end where the data actually lives. Continuous through zero and
  // monotonic for any y > -LOG_PIVOT (negatives below the floor clamp). With
  // LOG_PIVOT = 10000 the bottom two decades collapse to ~10% of the chart's
  // vertical span and the top three decades take the remaining ~90%.
  const LOG_PIVOT = 10000;
  const transformY = useMemo(() => {
    if (overviewChartScale !== 'log') return (y: number) => y;
    return (y: number) => Math.log10(Math.max(LOG_PIVOT + y, 1));
  }, [overviewChartScale]);
  const untransformY = useMemo(() => {
    if (overviewChartScale !== 'log') return (t: number) => t;
    return (t: number) => Math.pow(10, t) - LOG_PIVOT;
  }, [overviewChartScale]);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    const measure = () => {
      if (!rootRef.current) return;
      const parentRect = rootRef.current.getBoundingClientRect();
      setRootDimensions({
        width: parentRect.width,
        height: parentRect.height,
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  const userId = useSelector(selectUserId);
  const isDemo = useSelector(selectIsDemo);
  const activeCluster = useSelector(selectActiveCluster);

  // Demo mode aggregates a per-asset series client-side so the Settings
  // dropdown (exclusion + date window + start-balance rebase) can change the
  // chart instantly without a backend round-trip. The hook is inert in real
  // mode, so the only demo-aware logic this file carries is the `isDemo`
  // selector + the source-pick below.
  const { demoPortfolio, demoSettingsKey } = useDemoChartPortfolio();
  const { data: fastPortfolioReal } = useGetPortfolioChartFastQuery(
    { userId, clusterId: activeCluster },
    { skip: !userId || isDemo },
  );
  const { data: candlePortfolioReal } = useGetPortfolioChartQuery(
    { userId, clusterId: activeCluster },
    { skip: !userId || isDemo },
  );
  const fastPortfolio = isDemo ? demoPortfolio : fastPortfolioReal;
  const candlePortfolio = isDemo ? demoPortfolio : candlePortfolioReal;

  // Reset + restart the fallback window on each load (initial and cluster
  // switches) so every load gets the same grace period before the fast stopgap.
  const [showFastFallback, setShowFastFallback] = useState(false);
  useEffect(() => {
    setShowFastFallback(false);
    const timer = setTimeout(() => setShowFastFallback(true), FAST_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [userId, activeCluster]);

  const portfolio =
    candlePortfolio ?? (showFastFallback ? fastPortfolio : undefined);

  const isWithinTimeframe = useCallback(
    (timestamp: number) => {
      switch (overviewChartTimeframe) {
        case OverviewChartTimeframe.All:
          return true;
        case OverviewChartTimeframe.TwoYears:
          return moment().subtract(2, 'years').valueOf() <= timestamp;
        case OverviewChartTimeframe.OneYear:
          return moment().subtract(1, 'year').valueOf() <= timestamp;
        case OverviewChartTimeframe.SixMonths:
          return moment().subtract(6, 'months').valueOf() <= timestamp;
        case OverviewChartTimeframe.ThreeMonths:
          return moment().subtract(3, 'months').valueOf() <= timestamp;
        case OverviewChartTimeframe.TwoMonths:
          return moment().subtract(2, 'months').valueOf() <= timestamp;
        case OverviewChartTimeframe.OneMonth:
          return moment().subtract(1, 'month').valueOf() <= timestamp;
        case OverviewChartTimeframe.OneWeek:
          return moment().subtract(1, 'week').valueOf() <= timestamp;
        default:
          return true;
      }
    },
    [overviewChartTimeframe],
  );

  const {
    rawNapData,
    rawTotalStableData,
    nettoNapData,
    nettoTotalStableData,
    outperformanceData,
    rawCostBasisData,
  } = useMemo(() => {
    const totalPoints = portfolio?.dataPoints ?? [];
    const napPoints = portfolio?.napDataPoints ?? [];
    // netto = profit-since-inception, computed server-side (each sub minus its
    // own first value, forward-filled) so staggered sub starts stay correct.
    const nettoTotalPoints = portfolio?.nettoDataPoints ?? [];
    const nettoNapPoints = portfolio?.nettoNapDataPoints ?? [];

    // Outperformance = what trading added over holding, in dollars. Derived here
    // rather than server-side because both operands already arrive on every
    // source (candle route, fast route and the demo aggregator), so this needs
    // no endpoint, no cache key and no demo-mode special case.
    //
    // Joined on TIMESTAMP, never by index: the two series are resampled into
    // 2-day buckets independently and the fast route's disagree on 2 of 243
    // buckets, which an index join would subtract across.
    const outperformancePoints = subtractBaselineForwardFilled(
      nettoTotalPoints,
      nettoNapPoints,
    );

    // Build the timestamp-keyed records the rest of the chart already consumes,
    // windowed by the selected timeframe. In log mode we transform y once here;
    // the rest of the pipeline (max/min, stretch, ticks) runs in transformed
    // space and the tick label undoes the transform.
    const toRecord = (points: { timestamp: number; value: number }[]) =>
      points
        .filter((point) => isWithinTimeframe(point.timestamp))
        .reduce<
          Record<number, { x: number; y: number; hasIndicator: boolean }>
        >((acc, point) => {
          acc[point.timestamp] = {
            x: point.timestamp,
            y: transformY(point.value),
            hasIndicator: false,
          };
          return acc;
        }, {});

    return {
      rawTotalStableData: toRecord(totalPoints),
      rawNapData: toRecord(napPoints),
      nettoTotalStableData: toRecord(nettoTotalPoints),
      nettoNapData: toRecord(nettoNapPoints),
      outperformanceData: toRecord(outperformancePoints),
      rawCostBasisData: {} as Record<number, { x: number; y: number }>,
    };
  }, [portfolio, isWithinTimeframe, transformY]);

  const rawNapDataPoints = useMemo(
    () => Object.values(rawNapData).sort((a, b) => a.x - b.x),
    [rawNapData],
  );
  const nettoNapDataPoints = useMemo(
    () => Object.values(nettoNapData).sort((a, b) => a.x - b.x),
    [nettoNapData],
  );
  // Outperformance is measured AGAINST the hold line, so the hold line is not a
  // second series to draw — it is the zero of this one.
  const napDataPoints = useMemo(() => {
    if (overviewChartType === OverviewChartType.Outperformance) {
      return [];
    }

    return overviewChartType === OverviewChartType.ProfitOnly
      ? nettoNapDataPoints
      : rawNapDataPoints;
  }, [nettoNapDataPoints, overviewChartType, rawNapDataPoints]);
  const rawTotalStableDataPoints = useMemo(
    () => Object.values(rawTotalStableData).sort((a, b) => a.x - b.x),
    [rawTotalStableData],
  );
  const nettoTotalStableDataPoints = useMemo(
    () => Object.values(nettoTotalStableData).sort((a, b) => a.x - b.x),
    [nettoTotalStableData],
  );
  const outperformanceDataPoints = useMemo(
    () => Object.values(outperformanceData).sort((a, b) => a.x - b.x),
    [outperformanceData],
  );
  // The mode's series rides the SAME slot the other two modes use, so the log
  // transform, shared domain, stretch, y-ticks and axis width all apply to it
  // without a second code path.
  const totalStableDataPoints = useMemo(() => {
    switch (overviewChartType) {
      case OverviewChartType.Outperformance:
        return outperformanceDataPoints;
      case OverviewChartType.ProfitOnly:
        return nettoTotalStableDataPoints;
      default:
        return rawTotalStableDataPoints;
    }
  }, [
    nettoTotalStableDataPoints,
    outperformanceDataPoints,
    overviewChartType,
    rawTotalStableDataPoints,
  ]);
  const rawCostBasisDataPoints = useMemo(
    () => Object.values(rawCostBasisData).sort((a, b) => a.x - b.x),
    [rawCostBasisData],
  );

  const rawTotalStableDataPointsMax = useMemo(
    () => Math.max(...totalStableDataPoints.map((datum) => datum.y)),
    [totalStableDataPoints],
  );
  const rawNapDataPointsMax = useMemo(
    () => Math.max(...napDataPoints.map((datum) => datum.y)),
    [napDataPoints],
  );
  const rawCostBasisDataPointsMax = useMemo(
    () => Math.max(...rawCostBasisDataPoints.map((datum) => datum.y)),
    [rawCostBasisDataPoints],
  );

  const stableValueMax = useMemo(() => {
    return Math.max(
      rawNapDataPointsMax,
      rawTotalStableDataPointsMax,
      rawCostBasisDataPointsMax,
    );
  }, [
    rawNapDataPointsMax,
    rawTotalStableDataPointsMax,
    rawCostBasisDataPointsMax,
  ]);

  const totalStableDataOriginalDomain = useMemo(() => {
    return totalStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [totalStableDataPoints, stableValueMax]);
  const napDataOriginalDomain = useMemo(() => {
    return napDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [napDataPoints, stableValueMax]);
  const costBasisDataOriginalDomain = useMemo(() => {
    return rawCostBasisDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawCostBasisDataPoints, stableValueMax]);

  const stableValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...napDataOriginalDomain.map((d) => d.y),
      ...totalStableDataOriginalDomain.map((d) => d.y),
      ...costBasisDataOriginalDomain.map((d) => d.y),
    );
  }, [
    napDataOriginalDomain,
    totalStableDataOriginalDomain,
    costBasisDataOriginalDomain,
  ]);
  const stableValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      ...napDataOriginalDomain.map((d) => d.y),
      ...totalStableDataOriginalDomain.map((d) => d.y),
      ...costBasisDataOriginalDomain.map((d) => d.y),
    );
  }, [
    napDataOriginalDomain,
    totalStableDataOriginalDomain,
    costBasisDataOriginalDomain,
  ]);

  const napData = useMemo(() => {
    return napDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    napDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const totalStableData = useMemo(() => {
    return totalStableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    totalStableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const costBasisData = useMemo(() => {
    return costBasisDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    costBasisDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);

  const dataSets = useMemo(() => {
    // Only the first series is required. Outperformance draws one line, and
    // requiring the hold series here blanked the whole chart in that mode.
    if (!totalStableData?.length) {
      return [[]];
    }

    const data = [];

    if (totalStableData?.length) {
      data.push(totalStableData);
    }
    if (napData?.length) {
      data.push(napData);
    }
    if (costBasisData?.length) {
      data.push(costBasisData);
    }

    return data;
  }, [napData, totalStableData, costBasisData]);

  const isPositiveChart = useMemo(() => {
    if (!totalStableDataPoints?.length) {
      return true;
    }

    return (
      totalStableDataPoints[totalStableDataPoints.length - 1].y >=
      totalStableDataPoints[0].y
    );
  }, [totalStableDataPoints]);

  const getChartColor = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
          return {
            fill: `url(#${
              isPositiveChart ? 'positiveStableColor' : 'negativeStableColor'
            })`,
            stroke: isPositiveChart ? colors.mossGreen : colors.red,
          };

        case ChartType.NoAlgoritmicProgression:
          return {
            fill: 'url(#napColor)',
            stroke: colors.white,
          };
      }
    },
    [isPositiveChart],
  );

  const selectedChartTypes = useMemo(
    () => [ChartType.TotalStableBalance, ChartType.NoAlgoritmicProgression],
    [],
  );

  // const availableChartData = useMemo(
  //   () =>
  //     subscriptions.reduce((acc, curr) => acc + (curr.chartData ? 1 : 0), 0),
  //   [subscriptions],
  // );

  const { animatedDataPoints, domain } = useAnimatedChartData(
    useMemo(
      () =>
        `${overviewChartTimeframe}_${overviewChartType}_${subscriptions.length}_${demoSettingsKey}`,
      [
        overviewChartTimeframe,
        overviewChartType,
        subscriptions.length,
        demoSettingsKey,
      ],
    ),
    dataSets,
    useMemo(
      () => [ChartType.TotalStableBalance, ChartType.NoAlgoritmicProgression],
      [],
    ),
    false,
    2,
  );

  const hasData = useMemo(() => dataSets.some((d) => !!d.length), [dataSets]);

  const referenceLineTimestamp = TPT_REFERENCE_LINE.timestamp;
  const referenceLineLabel = TPT_REFERENCE_LINE.label;

  const startTPTReferenceLine = useMemo(() => {
    return buildReferenceLineData(referenceLineTimestamp, 0, 1);
  }, [referenceLineTimestamp]);

  const startTPTReferenceLabel = useMemo(() => {
    return { x: referenceLineTimestamp, y: 1, label: referenceLineLabel };
  }, [referenceLineTimestamp, referenceLineLabel]);

  const mrcReferenceLineTimestamp = MRC_REFERENCE_LINE.timestamp;
  const mrcReferenceLineLabel = MRC_REFERENCE_LINE.label;

  const startMRCReferenceLine = useMemo(() => {
    return buildReferenceLineData(mrcReferenceLineTimestamp, 0, 1);
  }, [mrcReferenceLineTimestamp]);

  const startMRCReferenceLabel = useMemo(() => {
    return { x: mrcReferenceLineTimestamp, y: 1, label: mrcReferenceLineLabel };
  }, [mrcReferenceLineTimestamp, mrcReferenceLineLabel]);

  // EXODUS has no fixed date: it marks the start of this chart's own x domain.
  const exodusReferenceLineTimestamp = useMemo(() => {
    const start = domain?.x?.[0];
    return typeof start === 'number' ? start : undefined;
  }, [domain?.x]);

  const startExodusReferenceLine = useMemo(() => {
    if (exodusReferenceLineTimestamp === undefined) {
      return [];
    }
    return buildReferenceLineData(exodusReferenceLineTimestamp, 0, 1);
  }, [exodusReferenceLineTimestamp]);

  const startExodusReferenceLabel = useMemo(() => {
    return {
      x: exodusReferenceLineTimestamp ?? 0,
      y: 1,
      label: EXODUS_REFERENCE_LINE.label,
    };
  }, [exodusReferenceLineTimestamp]);

  // AXIS
  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: colors.white },
    }),
    [],
  );

  const tickValues = useMemo(() => {
    if (!domain?.x[0]) {
      return [];
    }

    return createXTickValues(domain.x[0] as number, domain.x[1] as number, 10);
  }, [domain?.x]);

  const calculateYTickValue = useCallback(
    (t: any) => {
      const max = stableValueMax;
      const minO = stableValueOriginalDomainMin;
      const maxO = stableValueOriginalDomainMax;

      /** Reverse the stretch to get the (transformed-space) value */
      const y = maxO - (1 - t) * ((maxO - minO) * 1.05);
      const transformed = isNaN(y * max) ? 0 : y * max;
      const balance = untransformY(transformed);

      return `${formatPrice(
        balance,
        true,
        (maxO - minO) / maxO < 0.01 ? 2 : 0,
      )}`;
    },
    [
      stableValueMax,
      stableValueOriginalDomainMax,
      stableValueOriginalDomainMin,
      untransformY,
    ],
  );

  const yTicks = useMemo(() => {
    const dataPoints = [...totalStableDataPoints, ...napDataPoints];
    if (!dataPoints?.length) return [];

    const yValues = dataPoints.map((p) => p.y);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    if (minY === maxY) return [Math.round(minY)];

    const pushTick = (transformedY: number, acc: number[]) => {
      acc.push(
        stretchYValueOnSharedDomain(
          transformedY / stableValueMax,
          stableValueOriginalDomainMin,
          stableValueOriginalDomainMax,
        ),
      );
    };

    // Log mode rides the same custom curve as the data (log10(LOG_PIVOT + y)),
    // which crowds dollar decades near the bottom. Pick ticks from a 1-2-5
    // ladder of nice round dollar values, INCLUDING the negative side so
    // charts that dip below 0 (Profit Only on a drawdown) still label both
    // halves. Greedy-select with a min gap so they distribute like linear
    // ticks. If too few ladder values fit the visible range, fall back to
    // evenly-spaced positions so we never end up with 0–2 ticks.
    if (overviewChartScale === 'log') {
      const TICK_COUNT_TARGET = 7;
      const candidates: number[] = [0];
      for (let n = 0; n <= 12; n++) {
        const base = Math.pow(10, n);
        candidates.push(base, 2 * base, 5 * base);
        candidates.push(-base, -2 * base, -5 * base);
      }
      const inRange = candidates
        .map((v) => ({ v, t: transformY(v) }))
        .filter(({ t }) => t >= minY - 0.01 && t <= maxY + 0.01)
        .sort((a, b) => a.t - b.t);

      if (inRange.length >= 2) {
        const range = maxY - minY;
        const minGap = (range / (TICK_COUNT_TARGET - 1)) * 0.55;
        const picked: { v: number; t: number }[] = [inRange[0]];
        for (let i = 1; i < inRange.length; i++) {
          const last = picked[picked.length - 1];
          if (inRange[i].t - last.t >= minGap) picked.push(inRange[i]);
        }
        // Ensure the topmost tick reaches the top of the visible range.
        const lastInRange = inRange[inRange.length - 1];
        if (picked[picked.length - 1].v !== lastInRange.v) {
          picked.push(lastInRange);
        }
        if (picked.length >= 3) {
          const ticks: number[] = [];
          for (const p of picked) pushTick(p.t, ticks);
          return ticks;
        }
      }

      // Fallback when very few decade values fit (tight range): evenly space
      // TICK_COUNT_TARGET ticks across the transformed window. Labels won't
      // be round numbers but the axis will at least be readable.
      const evenStep = (maxY - minY) / (TICK_COUNT_TARGET - 1);
      const ticks: number[] = [];
      for (let i = 0; i < TICK_COUNT_TARGET; i++) {
        pushTick(minY + i * evenStep, ticks);
      }
      return ticks;
    }

    const range = maxY - minY;
    const roughStep = range / (8 - 1);

    // Determine step size based on magnitude, prioritizing "pretty" steps (e.g., 1000, 100, 10)
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const step = Math.max(1, Math.round(roughStep / magnitude) * magnitude);

    // Round minY down and maxY up to nearest step for clean ticks
    const start = Math.floor(minY / step) * step;
    const ticks: number[] = [];

    for (let tick = start; tick <= maxY + step; tick += step) {
      pushTick(Number(tick.toFixed(4)), ticks);
    }

    return ticks;
  }, [
    napDataPoints,
    totalStableDataPoints,
    stableValueMax,
    stableValueOriginalDomainMax,
    stableValueOriginalDomainMin,
    overviewChartScale,
    transformY,
  ]);

  const yAxisInformation = useMemo(() => {
    const longestTickWidth = calculateLongestTickWidth(yTicks, (t: number) =>
      calculateYTickValue(t),
    );
    const yAxisWidth = calculateYAxisWidth(longestTickWidth);
    return { width: yAxisWidth, longestTickWidth };
  }, [calculateYTickValue, yTicks]);

  const yAxisStyle = useMemo(() => {
    const width = yAxisInformation.width || 0;
    const longestTickWidth = yAxisInformation.longestTickWidth ?? 0;

    return {
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: {
        fontSize: 16,
        padding: (width - longestTickWidth) / 3,
        fill: colors.white,
      },
    };
  }, [yAxisInformation]);

  const chartPadding = useMemo(
    () => ({
      left: 30,
      top: 0,
      right: yAxisInformation.width ?? 60,
      bottom: 30,
    }),
    [yAxisInformation],
  );

  // We need some chart information for the
  useEffect(() => {
    if (
      !rawTotalStableDataPoints?.length ||
      rawTotalStableDataPoints.length < 2 ||
      !rawNapDataPoints?.length
    ) {
      return;
    }

    // rawTotalStableDataPoints / rawNapDataPoints carry transformed y values
    // (log-space in log mode). Outperformance, day change and the like must
    // be computed in dollar space, so untransform before doing any arithmetic.
    const latestStableValue = untransformY(
      rawTotalStableDataPoints[rawTotalStableDataPoints.length - 1].y,
    );
    const yesterdayStableValue = untransformY(
      rawTotalStableDataPoints[rawTotalStableDataPoints.length - 2].y,
    );
    const dayChange = latestStableValue - yesterdayStableValue;
    const dayChangeRelative = yesterdayStableValue
      ? (latestStableValue - yesterdayStableValue) / yesterdayStableValue
      : 0;

    const latestNapValue = untransformY(
      rawNapDataPoints[rawNapDataPoints.length - 1].y,
    );
    const napDifference = latestStableValue - latestNapValue;
    const napDifferenceRelative = latestNapValue
      ? (latestStableValue - latestNapValue) / latestNapValue
      : 0;

    const amountOfDays = Math.round(
      (rawTotalStableDataPoints[rawTotalStableDataPoints.length - 1].x -
        rawTotalStableDataPoints[0].x) /
        (1000 * 60 * 60 * 24),
    );

    const information = {
      dayChange,
      dayChangeRelative,
      napDifference,
      napDifferenceRelative,
      amountOfDays,
    };

    setSummaryInformation(information);
  }, [
    rawNapDataPoints,
    rawTotalStableDataPoints,
    setSummaryInformation,
    untransformY,
  ]);

  // ── Hover crosshair (HTML tooltip docked at the top + SVG cursor line) ──
  // Mirrors the Result page's main profit chart pattern from
  // ProfitCalculations/Content/SingleProfitCalculation/Result/Charts.
  const [cursorPixelX, setCursorPixelX] = useState<number | undefined>(
    undefined,
  );
  const handleCrosshairMove = useCallback((e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    setCursorPixelX(e.clientX - rect.left);
  }, []);
  const handleCrosshairLeave = useCallback(() => {
    setCursorPixelX(undefined);
  }, []);

  // Cursor pixel → timestamp via the chart's inner plot rectangle.
  const cursorTimestamp = useMemo(() => {
    if (cursorPixelX === undefined || !domain?.x || !rootDimensions)
      return null;
    const domainStart = domain.x[0] as number;
    const domainEnd = domain.x[1] as number;
    const plotWidth =
      rootDimensions.width - chartPadding.left - chartPadding.right;
    if (plotWidth <= 0) return null;
    const ratio = (cursorPixelX - chartPadding.left) / plotWidth;
    if (ratio < 0 || ratio > 1) return null;
    return domainStart + ratio * (domainEnd - domainStart);
  }, [cursorPixelX, domain, chartPadding, rootDimensions]);

  // For each visible series (trading + NAP) find the nearest point to the
  // cursor's timestamp. `animatedDataPoints` already holds the normalised
  // y-values used by the VictoryArea; we pair them with the raw $ values via
  // `raw{Total,Nap}DataPoints` so the tooltip shows dollars.
  const cursorSeries = useMemo(() => {
    if (cursorTimestamp == null) return [];
    const entries: Array<{
      x: number;
      y: number;
      fill: string;
      label: string;
      value: string;
    }> = [];

    const findBest = (
      normalised: { x: number; y: number }[],
      raw: { x: number; y: number }[],
    ) => {
      if (!normalised?.length) return null;
      let bestIndex = 0;
      for (let i = 1; i < normalised.length; i++) {
        if (
          Math.abs(normalised[i].x - cursorTimestamp) <
          Math.abs(normalised[bestIndex].x - cursorTimestamp)
        ) {
          bestIndex = i;
        }
      }
      return { normalised: normalised[bestIndex], raw: raw[bestIndex] };
    };

    // Pre/post-stretch values live in transformed space when the user picks
    // Log scale; convert back to dollars via untransformY before formatting.
    const formatRaw = (rawY: number) => {
      const dollars = untransformY(rawY);
      const formatted = formatPrice(dollars, true, 0);
      return valuesHidden ? maskMonetaryValue(formatted) : formatted;
    };

    const tradingBest = findBest(
      animatedDataPoints[0] ?? [],
      totalStableDataPoints,
    );
    if (tradingBest) {
      entries.push({
        x: tradingBest.normalised.x,
        y: tradingBest.normalised.y,
        fill: isPositiveChart ? colors.mossGreen : colors.red,
        label:
          overviewChartType === OverviewChartType.Outperformance
            ? 'Outperformance'
            : 'Trading',
        value: formatRaw(tradingBest.raw?.y ?? 0),
      });
    }

    const napBest = findBest(animatedDataPoints[1] ?? [], napDataPoints);
    if (napBest) {
      entries.push({
        x: napBest.normalised.x,
        y: napBest.normalised.y,
        fill: colors.white,
        label: 'Buy & Hold',
        value: formatRaw(napBest.raw?.y ?? 0),
      });
    }

    return entries;
  }, [
    cursorTimestamp,
    animatedDataPoints,
    totalStableDataPoints,
    napDataPoints,
    valuesHidden,
    isPositiveChart,
    untransformY,
    overviewChartType,
  ]);

  const cursorLineData = useMemo(() => {
    if (cursorTimestamp == null || !domain?.y) return [];
    const yMin = Number(domain.y[0]);
    const yMax = Number(domain.y[1]);
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) return [];
    return [
      { x: cursorTimestamp, y: yMin },
      { x: cursorTimestamp, y: yMax },
    ];
  }, [cursorTimestamp, domain]);

  const cursorDotData = useMemo(
    () =>
      cursorSeries
        .filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y))
        .map(({ x, y, fill }) => ({ x, y, fill })),
    [cursorSeries],
  );

  const showCrosshair =
    cursorTimestamp != null &&
    cursorPixelX !== undefined &&
    cursorSeries.length > 0 &&
    cursorLineData.length > 0;

  // Flip the tooltip to the left of the cursor near the right edge so it
  // doesn't run off the chart.
  const crosshairFlip = !!(
    rootDimensions &&
    cursorPixelX !== undefined &&
    cursorPixelX > rootDimensions.width - CROSSHAIR_TOOLTIP_WIDTH - 30
  );

  return (
    <div
      ref={rootRef}
      onMouseMove={handleCrosshairMove}
      onMouseLeave={handleCrosshairLeave}
      style={{
        position: 'relative',
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        height: '100%',
        width: '100%',
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          {/* <linearGradient
            id={`botColor_${subscription.baseAsset}`}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={botColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={botColor} stopOpacity={0.04} />
          </linearGradient> */}
          <linearGradient id="napColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.white} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.white} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="negativeMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <ReferenceLinePatterns />
        </defs>
      </svg>

      {!!rootDimensions && !!hasData && (
        <>
          <VictoryChart
            height={rootDimensions.height}
            width={rootDimensions.width}
            scale="linear"
            padding={chartPadding}
            domain={domain}
            domainPadding={{ y: 10 }}
          >
            <VictoryAxis
              dependentAxis
              style={yAxisStyle}
              orientation={'right'}
              tickFormat={(t: number) =>
                valuesHidden
                  ? maskMonetaryValue(calculateYTickValue(t))
                  : calculateYTickValue(t)
              }
              width={yAxisInformation.width}
              tickValues={yTicks}
            />
            <VictoryAxis
              tickFormat={calculateTickValue}
              style={axisStyle}
              tickValues={tickValues}
              tickCount={10}
              fixLabelOverlap
            />
            {animatedDataPoints.slice(0, 2).map((selectedDataPoint, index) => (
              <VictoryArea
                key={`chart_${index}`}
                data={selectedDataPoint}
                interpolation="monotoneX"
                style={{
                  data: {
                    ...getChartColor(selectedChartTypes[index]),
                    strokeWidth: 1,
                  },
                }}
              />
            ))}
            {/* Vertical cursor line + highlight dots for the crosshair —
             *  pointer events off so they never steal a hover. */}
            {showCrosshair && (
              <VictoryLine
                data={cursorLineData}
                style={{
                  data: {
                    stroke: 'rgba(255,255,255,0.55)',
                    strokeWidth: 1,
                    pointerEvents: 'none',
                  },
                }}
              />
            )}
            {showCrosshair && (
              <VictoryScatter
                data={cursorDotData}
                size={4}
                style={{
                  data: {
                    fill: ({ datum }) => datum.fill,
                    stroke: '#0c0e12',
                    strokeWidth: 1.5,
                    pointerEvents: 'none',
                  },
                }}
              />
            )}
            <VictoryLine
              data={startTPTReferenceLine}
              style={{
                data: {
                  stroke: `url(#${TPT_REFERENCE_LINE.patternId})`,
                  strokeWidth: 5,
                },
              }}
            />
            <VictoryScatter
              data={[startTPTReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: TPT_REFERENCE_LINE.color,
                    fontWeight: 'bold',
                    fontSize: 10,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-8}
                />
              }
            />
            <VictoryLine
              data={startMRCReferenceLine}
              style={{
                data: {
                  stroke: `url(#${MRC_REFERENCE_LINE.patternId})`,
                  strokeWidth: 5,
                },
              }}
            />
            <VictoryScatter
              data={[startMRCReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: MRC_REFERENCE_LINE.color,
                    fontWeight: 'bold',
                    fontSize: 10,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-8}
                />
              }
            />
            {/* EXODUS sits on the domain's left edge, so its label reads to
             *  the right of the line instead of to the left. */}
            {!!startExodusReferenceLine.length && (
              <VictoryLine
                data={startExodusReferenceLine}
                style={{
                  data: {
                    stroke: `url(#${EXODUS_REFERENCE_LINE.patternId})`,
                    strokeWidth: 5,
                  },
                }}
              />
            )}
            {!!startExodusReferenceLine.length && (
              <VictoryScatter
                data={[startExodusReferenceLabel]}
                size={0}
                labels={({ datum }) => datum.label}
                labelComponent={
                  <VictoryLabel
                    style={{
                      fill: EXODUS_REFERENCE_LINE.color,
                      fontWeight: 'bold',
                      fontSize: 10,
                      opacity: 0.7,
                    }}
                    textAnchor="start"
                    dy={8}
                    dx={8}
                  />
                }
              />
            )}
            {/* {animatedDataPoints.slice(-1).map((selectedDataPoint, index) => (
              <VictoryLine
                key={`chart_${index}`}
                data={selectedDataPoint}
                style={{
                  data: {
                    stroke: colors.orange,
                    strokeWidth: 1,
                  },
                }}
              />
            ))} */}
          </VictoryChart>
        </>
      )}

      {/* HTML crosshair tooltip — docked at the top of the chart, anchored
       *  to the cursor x. Mirrors the Result page main profit chart. */}
      {showCrosshair && cursorPixelX !== undefined && (
        <CrosshairTooltip
          title={moment(cursorTimestamp ?? undefined).format('DD MMM YYYY')}
          accent="#eef0f2"
          rows={cursorSeries.map((entry) => ({
            label: entry.label,
            value: entry.value,
            color: entry.fill,
          }))}
          left={cursorPixelX}
          top={8}
          flip={crosshairFlip}
        />
      )}

      {!!rootDimensions && !hasData && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            // Reserve the bottom strip the stats are overlaid on (bottom:32px +
            // ~44px content) so the icon centers in the visible empty area, not
            // the full container.
            bottom: 80,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingIcons.Grid width={40} height={40} />
        </div>
      )}
    </div>
  );
};

export default SummaryChart;
